import { css, Theme, useTheme } from '@emotion/react'
import { FC, useCallback } from 'react'
import { useField } from 'react-final-form'

import TextField from '../molecules/TextField'

export type Props = {
  isConfirm: boolean
  required?: boolean
  apiKey: string
}

const AddressField: FC<Props> = ({ isConfirm, required, apiKey }) => {
  const theme = useTheme()
  const {
    input: { value: postalValue },
  } = useField('postal')
  const {
    input: { onChange: addressCityOnChange },
  } = useField('addressCity')
  const {
    input: { onChange: addressHomeNumberOnChange },
  } = useField('addressHomeNumber')
  const {
    input: { value: addressBuildingNameValue },
  } = useField('addressBuildingName')

  const handleAutoInput = useCallback(async () => {
    if (!postalValue) return
    try {
      const response = await fetch(
        `https://apis.postcode-jp.com/api/v5/postcodes/${postalValue}?apikey=${apiKey}`
      )
      const json = await response.json()
      if (json.length < 1) return
      const { pref, city, town } = json[0]
      addressCityOnChange(pref)
      addressHomeNumberOnChange(`${city}${town}`)
    } catch (error) {
      console.log(error)
    }
  }, [addressCityOnChange, addressHomeNumberOnChange, apiKey, postalValue])

  return (
    <div css={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
      <div css={{ display: 'flex', gap: '10px' }}>
        <div css={postalWrapperStyle}>
          <TextField
            isConfirm={isConfirm}
            name='postal'
            label='郵便番号'
            helperText='ハイフンなしで入力してください'
            placeholder='1234567'
            required={required}
            parse={(value) => {
              const onlyNumbers = value.replace(/[^\d]/g, '')

              return onlyNumbers
            }}
          />
        </div>
        {!isConfirm && (
          <div css={buttonWrapperStyle}>
            <button
              css={buttonStyle(theme)}
              type='button'
              onClick={handleAutoInput}
              disabled={!postalValue}
            >
              <span css={mdDownHiddenStyle}>郵便番号から</span>
              <span css={smDownHiddenStyle}>住所を</span>自動入力
            </button>
          </div>
        )}
      </div>
      <div>
        <TextField
          isConfirm={isConfirm}
          name='addressCity'
          label='都道府県'
          placeholder='東京都'
          required={required}
          validate={(value) => {
            if (
              ![
                '北海道',
                '青森県',
                '岩手県',
                '宮城県',
                '秋田県',
                '山形県',
                '福島県',
                '茨城県',
                '栃木県',
                '群馬県',
                '埼玉県',
                '千葉県',
                '東京都',
                '神奈川県',
                '新潟県',
                '富山県',
                '石川県',
                '福井県',
                '山梨県',
                '長野県',
                '岐阜県',
                '静岡県',
                '愛知県',
                '三重県',
                '滋賀県',
                '京都府',
                '大阪府',
                '兵庫県',
                '奈良県',
                '和歌山県',
                '鳥取県',
                '島根県',
                '岡山県',
                '広島県',
                '山口県',
                '徳島県',
                '香川県',
                '愛媛県',
                '高知県',
                '福岡県',
                '佐賀県',
                '長崎県',
                '熊本県',
                '大分県',
                '宮崎県',
                '鹿児島県',
                '沖縄県',
              ].includes(value)
            ) {
              return `都道府県名に誤りがあります`
            }
          }}
        />
      </div>
      <div>
        <TextField
          isConfirm={isConfirm}
          name='addressHomeNumber'
          label='市区町村番地'
          placeholder='渋谷区御金町1-2-3'
          required={required}
        />
      </div>
      {isConfirm && !addressBuildingNameValue ? null : (
        <div>
          <TextField
            isConfirm={isConfirm}
            name='addressBuildingName'
            label='マンション・ビル名'
            placeholder='OKANEビル 101'
          />
        </div>
      )}
    </div>
  )
}

AddressField.displayName = 'address'

export default AddressField

const postalWrapperStyle = css`
  max-width: 17em;
  flex-grow: 1;
  font-size: 14px;
`

const buttonWrapperStyle = css`
  padding-top: 34px;
  font-size: 14px;

  @media (max-width: 767px) {
    padding-top: 31px;
  }
`

const mdDownHiddenStyle = css`
  @media (max-width: 767px) {
    display: none;
  }
`

const smDownHiddenStyle = css`
  @media (max-width: 400px) {
    display: none;
  }
`

const buttonStyle = (theme: Theme) => css`
  padding: 12px 15px;
  border-radius: 10px;
  background-color: ${theme.color.yellow};
  color: ${theme.color.black};
  font-size: inherit;
  font-weight: bold;
  line-height: calc(24 / 14);

  @media (max-width: 767px) {
    padding-block: 13px;
    padding: 18px 15px;
  }

  &:focus {
    box-shadow: 0 0 0 4px #daedf8, 0 0 0 6px ${theme.color.yellow};
  }
`
